import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../ui/Icon';
import Reveal from '../ui/Reveal';
import PatternAccent from '../ui/PatternAccent';
import lesehan from '../../assets/home/lesehan.webp';

// The year itself belongs on the hero tag and in the founders' own words.
// Everywhere else, say the span instead — and derive it, so the page does
// not quietly become wrong next January.
const FOUNDED = 1963;
const yearsServing = () => new Date().getFullYear() - FOUNDED;

const fmtPrice = (item) => {
  if (item.price_display) return item.price_display;
  const v = item.price_min ?? item.price_max;
  if (v == null) return null;
  return new Intl.NumberFormat('id-ID', {
    style: 'currency', currency: 'IDR', minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(v);
};

export default function FeaturedMenu({ items, branchCount, loading }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.split('-')[0] || 'id';
  const localized = (row, field) =>
    row[`${field}_${lang}`] || row[`${field}_id`] || row[`${field}_en`] || '';

  return (
    <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 md:py-20 lg:px-8">
      <div className="grid gap-6 lg:grid-cols-[1.35fr_1fr]">
        {/* ---- featured dishes ---- */}
        <div>
          <Reveal>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-600">
              {t('home.menu.eyebrow', 'Featured Menu')}
            </span>
          </Reveal>

          <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-4">
            {loading
              ? [0, 1, 2, 3].map((i) => (
                <div key={i} className="h-64 animate-pulse rounded-2xl bg-gray-100" />
              ))
              : items.map((item, i) => (
                <Reveal key={item.id} delay={i * 0.07} className="h-full">
                  <article className="flex h-full flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm transition-shadow hover:shadow-md">
                    <div className="aspect-square overflow-hidden bg-gray-50">
                      {(item.thumbnail_url || item.image_url) && (
                        <img
                          src={item.thumbnail_url || item.image_url}
                          alt={localized(item, 'name')}
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
                        />
                      )}
                    </div>
                    <div className="flex flex-1 flex-col p-3">
                      <h3 className="text-sm font-semibold leading-snug text-gray-900">
                        {localized(item, 'name')}
                      </h3>
                      {localized(item, 'description') && (
                        <p className="mt-1 line-clamp-3 text-xs leading-relaxed text-gray-500">
                          {localized(item, 'description')}
                        </p>
                      )}
                      {fmtPrice(item) && (
                        <p className="mt-auto pt-2 text-sm font-bold text-primary-600">{fmtPrice(item)}</p>
                      )}
                    </div>
                  </article>
                </Reveal>
              ))}
          </div>

          <div className="mt-6 flex justify-center">
            <Link
              to="/menu"
              className="inline-flex items-center gap-2 rounded-lg border border-primary-200 px-6 py-2.5 text-sm font-medium text-primary-700 transition-colors hover:border-primary-400 hover:bg-primary-50"
            >
              {t('home.menu.viewFull', 'View Full Menu')}
              <Icon name="arrowRight" className="h-4 w-4" />
            </Link>
          </div>
        </div>

        {/* ---- our story ---- */}
        <Reveal from="right">
          <div className="relative flex h-full flex-col overflow-hidden rounded-2xl border border-gray-100 bg-[#fdf8f4] shadow-sm">
            <PatternAccent at="top-right" className="w-40" opacity={0.1} />
            <div className="relative z-10 p-6 md:p-7">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-600">
                {t('home.story.eyebrow', 'Our Story')}
              </span>
              <h2 className="mt-2 font-serif text-2xl font-bold leading-snug text-gray-900">
                {t('home.story.title', 'From West Java, with Heart.')}
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-gray-600">
                {t('home.story.body1')}
              </p>
              <p className="mt-2.5 text-sm leading-relaxed text-gray-600">
                {t('home.story.body2')}
              </p>
              {/* Hands off to the founders section below rather than
                  jumping straight to /brand, which the founders' own CTA
                  already does. */}
              <a href="#founders" className="btn-primary mt-5 inline-block text-sm">
                {t('home.story.cta', 'Learn Our Story')}
              </a>
            </div>

            <div className="mt-auto">
              <img
                src={lesehan}
                alt={t('home.story.imageAlt', 'Guests eating lesehan style, seated on the floor')}
                loading="lazy"
                className="h-52 w-full object-cover md:h-60"
              />
              {/* Beneath the photograph, not over it. Floating the stats on
                  the lower third covered the people eating, which is the
                  whole subject of the picture. */}
              <div className="grid grid-cols-3 divide-x divide-gray-100 border-t border-gray-100 bg-white py-3.5">
                {[
                  ['building', branchCount ? `${branchCount}+` : '—', t('home.story.statBranches', 'Branches')],
                  ['calendar', `${yearsServing()}`, t('home.story.statYears', 'Years serving')],
                  ['mapPin', t('home.story.statHomeValue', 'West Java'), t('home.story.statHome', 'Our Home')],
                ].map(([icon, value, label]) => (
                  <div key={label} className="flex flex-col items-center px-1 text-center">
                    <Icon name={icon} className="h-4 w-4 text-primary-500" />
                    <span className="mt-1 text-sm font-bold text-gray-900">{value}</span>
                    <span className="text-[10px] uppercase tracking-wide text-gray-500">{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
